import { useCallback, useRef, useState } from 'react';
import { useBrowserPosition } from '@voll-intelligence/intelligence-ui-kit';

import type { HotelPlaceOption, HotelPlannerPort } from '@voll-intelligence/genui-contract';

export type MyLocationState = 'idle' | 'loading' | 'error';

/**
 * "Minha localização" do campo de destino: a posição do navegador
 * (`useBrowserPosition`, o hook comum das verticais) trocada por um lugar REAL
 * pelo port `findHotelPlaceByCoordinate` — a mesma `HotelPlaceOption` de um
 * pick por texto; quem a aplica (`applyMyLocation`) marca o valor da barra com
 * `placeSource: 'geolocation'` (o veto de recente do BFF). Nada de lugar sintético:
 * daqui em diante o organismo trata o resultado como qualquer outra escolha.
 *
 * Recusa/timeout do navegador, port ausente, ponto sem lugar e BFF fora caem
 * todos em `state: 'error'` e `null` — o campo segue usável por texto, e a
 * linha mostra a dica. O estado é do último pedido: um novo clique limpa o
 * erro.
 *
 * `cancel` descarta o pedido em voo (a barra chama no pick do Onde: a escolha
 * manual vence a posição): ele resolve `null` e não mexe mais no estado, que
 * volta a `idle` na hora. Um clique novo também supera o anterior.
 */
export function useMyLocation(findByCoordinate: HotelPlannerPort['findHotelPlaceByCoordinate']): {
  state: MyLocationState;
  request: () => Promise<HotelPlaceOption | null>;
  cancel: () => void;
} {
  const readPosition = useBrowserPosition().request;
  const [state, setState] = useState<MyLocationState>('idle');
  const generation = useRef(0);
  const request = useCallback(async (): Promise<HotelPlaceOption | null> => {
    const mine = ++generation.current;
    setState('loading');
    const coords = findByCoordinate ? await readPosition() : null;
    let option: HotelPlaceOption | null = null;
    if (coords && findByCoordinate && mine === generation.current) {
      try {
        option = await findByCoordinate(coords);
      } catch {
        option = null;
      }
    }
    if (mine !== generation.current) return null;
    setState(option ? 'idle' : 'error');
    return option;
  }, [findByCoordinate, readPosition]);
  const cancel = useCallback(() => {
    generation.current += 1;
    setState((current) => (current === 'loading' ? 'idle' : current));
  }, []);
  return { state, request, cancel };
}
